import React from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, FileText, Eye, CookieIcon, AlertTriangle } from "lucide-react";

/* ── Tiny composables, all on-brand ───────────────────────────────────── */

function Section({ id, eyebrow, title, icon: Icon, children, accent = "lilac" }) {
  const accentBg = { lilac: "bg-lilac", mustard: "bg-mustard/70", powder: "bg-powder", linen: "bg-linen" }[accent] || "bg-lilac";
  return (
    <section id={id} className="scroll-mt-24">
      <div className="flex items-center gap-3 mb-5">
        <span className={`w-10 h-10 rounded-full ${accentBg} border-2 border-black flex items-center justify-center shrink-0`}>
          {Icon && <Icon className="w-5 h-5 text-black" strokeWidth={2}/>}
        </span>
        <p className="lif-overline">{eyebrow}</p>
      </div>
      <h2 className="text-3xl md:text-4xl text-black mb-6 leading-tight"
          style={{ fontFamily: '"Bringbold", Georgia, serif' }}>
        {title}
      </h2>
      <div className="space-y-6 text-black/90 leading-relaxed text-[15.5px] md:text-base">
        {children}
      </div>
    </section>
  );
}

function QA({ q, children }) {
  return (
    <div>
      <p className="text-black font-semibold mb-1.5">{q}</p>
      <div className="text-black/85 space-y-3">{children}</div>
    </div>
  );
}

const RIGHTS = [
  { t: "Access", d: "Request a copy of the personal data we hold about you." },
  { t: "Correction", d: "Ask us to fix anything that's inaccurate or out of date." },
  { t: "Deletion", d: "Request that we delete your data, subject to any legal obligations we may have to retain it." },
  { t: "Portability", d: "Ask for your data in a structured, machine-readable format so you can take it elsewhere." },
  { t: "Objection", d: "Object to how we're using your data, including for analytics or communications." },
  { t: "Withdrawal", d: "Withdraw consent at any time where consent is the basis for our processing." },
];

const TOC = [
  { id: "what-we-collect", label: "What we collect and why" },
  { id: "your-rights", label: "Your rights over your data" },
  { id: "how-long", label: "How long we keep your data" },
  { id: "cookies", label: "Cookies and tracking" },
  { id: "breach", label: "If something goes wrong" },
];

export default function Privacy() {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="privacy-page">
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <p className="lif-overline mb-5">Trust, in full</p>
        <h1 className="text-5xl md:text-7xl text-black leading-[0.98] mb-10"
            style={{ fontFamily: '"Bringbold", Georgia, serif' }}>
          Privacy, security<br/>and data safety.
        </h1>

        <div className="p-8 md:p-10 rounded-[1.5rem] bg-powder border-2 border-black mb-14">
          <p className="text-black/90 leading-relaxed text-lg">
            Sharing your story with the world takes trust. <strong>Love, In Full</strong> is built for lovers
            who deserve to do that safely, which means we've been deliberate about how we handle your
            data from the ground up.
          </p>
          <p className="text-black/90 leading-relaxed mt-4">
            This page explains exactly what we collect, how it's protected, and what you can do with
            it. No jargon, no small-print surprises. We want you to understand this, fully.
          </p>
        </div>

        {/* ── On-page TOC ──────────────────────────────────────────────── */}
        <nav aria-label="On this page" className="mb-16 p-7 rounded-[1.25rem] bg-lilac border-2 border-black">
          <p className="lif-overline mb-3">On this page</p>
          <ol className="space-y-1.5">
            {TOC.map((t, i) => (
              <li key={t.id}>
                <a href={`#${t.id}`}
                   className="flex items-baseline gap-3 text-black hover:text-wine transition group"
                   data-testid={`toc-${t.id}`}>
                  <span className="text-xs tabular-nums text-black/55 group-hover:text-wine">0{i + 1}</span>
                  <span className="underline-offset-4 group-hover:underline">{t.label}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-20">
          {/* ── 1. What we collect ────────────────────────────────────── */}
          <Section id="what-we-collect" eyebrow="01 — Information" title="What we collect and why" icon={FileText} accent="mustard">
            <QA q="What information do you collect from lovers?">
              <p>
                When you create a profile, we collect the information you give us: your name, email
                address, photos, and any details you add to your listing. We also collect basic usage
                data, things like which pages you visit and how you navigate the platform, so we can
                understand what's working and what isn't.
              </p>
            </QA>
            <QA q="What do you collect from vendors?">
              <p>
                Business name, contact details, profile content, and the information submitted during
                your application. Once you're active on the platform, we log standard usage activity
                linked to your account.
              </p>
            </QA>
            <QA q="Do you collect anything automatically?">
              <p>
                Yes. Like most platforms, we collect some technical data automatically when you use
                the site. This includes your IP address, browser type, device information, and pages
                visited. We use this for platform security and to understand general usage patterns.
                We may use cookies or similar technologies for analytics; you can manage these through
                your browser settings.
              </p>
            </QA>
            <QA q="Why do you collect all of this?">
              <p>
                To run the platform: creating and managing your account, processing your application,
                sending you relevant communications, and keeping everything secure. We don't sell your
                data. We don't use it for advertising. The information you share here stays here, used
                only for the purposes you'd expect.
              </p>
            </QA>

            <div className="mt-2 p-6 md:p-7 rounded-[1.25rem] bg-linen border-2 border-black">
              <p className="lif-overline mb-2">A note on sensitive information</p>
              <p className="text-black/90 leading-relaxed">
                Details about your relationship, identity, and who you love are legally classified as
                sensitive personal data in most jurisdictions, and we treat them that way. We collect
                only what's needed for your listing, we store it securely, and we don't share it with
                third parties beyond what's described on this page. If you ever have concerns about
                specific information on your profile, you can contact us directly and we'll work
                through it with you. At every stage, what you share with us is up to you.
              </p>
            </div>
          </Section>

          {/* ── 2. Your rights ────────────────────────────────────────── */}
          <Section id="your-rights" eyebrow="02 — Control" title="Your rights over your data" icon={ShieldCheck} accent="lilac">
            <QA q="What rights do I have?">
              <p>
                Regardless of where you're based, you have rights over the personal data we hold
                about you. Under Australian Privacy Law, UK GDPR, and US state laws (including CCPA
                for California residents), those rights include the following:
              </p>
            </QA>

            <ul className="!list-none !pl-0 grid sm:grid-cols-2 gap-4 mt-2">
              {RIGHTS.map((r) => (
                <li key={r.t} className="p-5 rounded-[1rem] bg-white border-2 border-black">
                  <p className="text-lg text-black mb-1.5"
                     style={{ fontFamily: '"Bringbold", Georgia, serif' }}>
                    {r.t}
                  </p>
                  <p className="text-sm text-black/80 leading-relaxed">{r.d}</p>
                </li>
              ))}
            </ul>

            <QA q="How do I exercise these rights?">
              <p>
                Email us at <a href="mailto:hello@loveinfull.co" className="lif-link">hello@loveinfull.co</a> with
                your request. We'll respond within 30 days. In most cases we'll just need to verify
                who you are before we proceed.
              </p>
            </QA>
          </Section>

          {/* ── 3. How long ───────────────────────────────────────────── */}
          <Section id="how-long" eyebrow="03 — Retention" title="How long we keep your data" icon={Lock} accent="powder">
            <QA q="What happens to my data if I close my account?">
              <p>
                When you close your account or request deletion, we remove your personal data from
                our active systems within 30 days. Some information may be retained for a short
                period beyond that where we're legally required to keep it, for example, for tax or
                compliance purposes, after which it's permanently deleted.
              </p>
            </QA>
            <QA q="What about vendor applications that don't get approved?">
              <p>
                If your application is declined, we retain basic application details for 6 months in
                case of a dispute or reapplication, then delete them.
              </p>
            </QA>
            <QA q="How long is usage data kept?">
              <p>
                Anonymised analytics data may be retained longer for platform improvement purposes.
                This is stripped of any personally identifying information before it's stored.
              </p>
            </QA>
          </Section>

          {/* ── 4. Cookies ────────────────────────────────────────────── */}
          <Section id="cookies" eyebrow="04 — Tracking" title="Cookies and tracking" icon={CookieIcon} accent="mustard">
            <QA q="Do you use cookies?">
              <p>
                Yes. We use essential cookies to keep you logged in and make the platform function
                properly. We also use analytics cookies to understand how people use the site,
                things like which pages are visited most and where people drop off. We don't use
                advertising cookies or sell data to ad networks.
              </p>
            </QA>
            <QA q="Can I opt out?">
              <p>
                You can manage cookie preferences through your browser settings at any time.
                Disabling non-essential cookies won't affect your ability to use the platform,
                though some features may behave slightly differently.
              </p>
            </QA>
          </Section>

          {/* ── 5. If something goes wrong ────────────────────────────── */}
          <Section id="breach" eyebrow="05 — Accountability" title="If something goes wrong" icon={AlertTriangle} accent="lilac">
            <QA q="What happens in the event of a data breach?">
              <p>
                If we ever become aware of a breach that affects your personal data, we'll notify you
                promptly, within 72 hours of becoming aware, where required by law. We will include a
                clear explanation of what happened, what data was involved, and what we're doing
                about it. We'll also report to the relevant authority (the OAIC in Australia, the ICO
                in the UK) as required.
              </p>
            </QA>
            <QA q="What if I have a complaint?">
              <p>
                Start by emailing us at <a href="mailto:hello@loveinfull.co" className="lif-link">hello@loveinfull.co</a> —
                we'd much rather hear from you directly and sort it out. If you're not satisfied with
                our response, you have the right to escalate to your local data protection authority:
                the Office of the Australian Information Commissioner (OAIC), the UK Information
                Commissioner's Office (ICO), or the relevant US state authority depending on where
                you're based.
              </p>
            </QA>
          </Section>
        </div>

        {/* ── Closing CTA ─────────────────────────────────────────────── */}
        <div className="mt-20 p-9 md:p-12 rounded-[1.5rem] bg-lilac border-2 border-black">
          <p className="lif-overline mb-4">Still have questions?</p>
          <h2 className="text-3xl md:text-4xl text-black mb-5"
              style={{ fontFamily: '"Bringbold", Georgia, serif' }}>
            We're real humans on the other end.
          </h2>
          <p className="text-black/90 mb-8 max-w-2xl leading-relaxed">
            If anything on this page feels unclear or you'd like to talk something through, email us
            directly. We promise a thoughtful reply, we're real people on the other end.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="mailto:hello@loveinfull.co" className="lif-btn-cream" data-testid="privacy-contact-btn">
              Email hello@loveinfull.co
            </a>
            <Link to="/about" className="lif-btn-powder">Read more about us</Link>
          </div>
        </div>

        <p className="mt-10 text-xs text-black/60">
          <Eye className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" strokeWidth={2}/>
          Last updated June 2026. We'll update this page whenever anything material changes, and
          we'll let active members know directly.
        </p>
      </div>
    </Layout>
  );
}
